import { Formik, useField, useFormikContext } from 'formik';
import {
  ActorRole,
  AssessmentExpiryOptionUnit,
  CompanyPageCompanyFragment,
  ProjectTeamEmailMode,
  ReasonableAdjustmentsOptionKind,
  ReportSubType,
  useCompanyPageUpdateCompanyMutation,
} from 'generated/graphql';
import { actorRoleLabels, defaultActorRoles } from 'helpers/actorRoles';
import {
  projectTeamEmailModeOptions,
  projectTeamEmailToOptionsMap,
} from 'helpers/projectTeamEmailMode';
import { useEffect, useMemo, useState } from 'react';
import {
  enableDisableRadioButtonOptions,
  EnableDisableValue,
  generateRadioButtonOptions,
  yesNoRadioButtonOptions,
} from 'views/Company/CompanyView/CompanyView.helpers';
import { getAssessmentExpiryUnitString } from 'views/Company/CompanyView/components/AssessmentsSection/AssessmentsSection.helpers';
import {
  assessmentExpiryDurationOptions,
  assessmentExpiryEnablingNotificationMessages,
  dataTestId,
  validationSchema,
} from 'views/Company/CompanyView/components/AssessmentsSectionForm/AssessmentsSectionForm.helpers';
import {
  AssessmentsSectionFormProps,
  AssessmentsSectionFormValues,
  NotificationVisibility,
} from 'views/Company/CompanyView/components/AssessmentsSectionForm/AssessmentsSectionForm.types';
import ReasonableAdjustments from 'views/Company/CompanyView/components/ReasonableAdjustmentsRadioButtonGroup/ReasonableAdjustmentsRadioButtonGroup';
import ReportSelect from 'views/Company/CompanyView/components/ReportSelect/ReportSelect';
import {
  EditFormWrapper,
  FormikCheckboxFormField,
  FormikMultiselectFormField,
  FormikRadioButtonGroup,
  FormikSelectFormField,
  InlineNotification,
  NotificationType,
  TMultiselectOption,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from './AssessmentsSectionForm.module.scss';

export function parseActorRolesForSelect(): TMultiselectOption[] {
  return defaultActorRoles.map(actorRole => ({
    label: actorRoleLabels[actorRole],
    value: actorRole,
  }));
}

export default function AssessmentsSectionForm({
  defaultManagerReportId,
  defaultCandidateReportId,
  company,
  onCancelClick,
  onSave,
}: AssessmentsSectionFormProps) {
  const { handleMsgType } = useNotification();

  const [editCompanyMutation] = useCompanyPageUpdateCompanyMutation();

  const handleSave = (values: AssessmentsSectionFormValues) => {
    let reasonableAdjustmentsOptionValue = undefined;
    if (
      values.reasonableAdjustments ===
      ReasonableAdjustmentsOptionKind.ContactCompanyUrl
    ) {
      reasonableAdjustmentsOptionValue = values.contactCompanyWebsiteUrl;
    } else if (
      values.reasonableAdjustments ===
      ReasonableAdjustmentsOptionKind.ContactCompanyEmail
    ) {
      reasonableAdjustmentsOptionValue = values.contactCompanyEmail;
    }

    const assessmentExpiryOptionValue =
      values.assessmentExpiry === EnableDisableValue.Enabled
        ? {
            duration: values.assessmentExpiryDuration?.value ?? 0,
            unit: AssessmentExpiryOptionUnit.Day,
          }
        : null;

    editCompanyMutation({
      variables: {
        id: company.id,
        name: company.name,
        managerReportId: values.managerReport.value
          ? values.managerReport.value
          : undefined,
        candidateReportId: values.candidateReport.value
          ? values.candidateReport.value
          : undefined,
        reasonableAdjustmentsOption: {
          kind: values.reasonableAdjustments,
          value: reasonableAdjustmentsOptionValue,
        },
        assessmentExpiryOption: assessmentExpiryOptionValue,
        generateManagerReport:
          values.generateManagerReport === EnableDisableValue.Enabled,
        generateCandidateReport:
          values.generateCandidateReport === EnableDisableValue.Enabled,
        emailCandidateReport: values.emailCandidateReport,
        hideReports: values.hideReports === EnableDisableValue.Disabled,
        projectTeamEmailSettings: {
          mode:
            values.projectTeamEmailSettingsMode.value ??
            ProjectTeamEmailMode.Disabled,
          actorRoles:
            values.projectTeamEmailRoles.map(role => role.value as ActorRole) ??
            [],
        },
        areCognitiveTestsBlocked:
          values.cognitiveTests === EnableDisableValue.Disabled,
        areMultiAssessmentStagesEnabled:
          values.multiAssessmentStages === EnableDisableValue.Enabled,
        isPersonalityTestMandatory:
          values.mandatoryPersonalityTest === EnableDisableValue.Enabled,
        isAiInterviewEnabled: values.aiInterview === EnableDisableValue.Enabled,
      },
      onCompleted: () => {
        handleMsgType({
          type: TNotification.success,
          title: 'Assessment settings updated.',
          dismissable: true,
        });
        onSave();
      },
      onError: () => {
        handleMsgType({
          type: TNotification.error,
          title: 'Assessment settings could not be updated',
          message: 'Something went wrong. Please try again.',
          dismissable: true,
        });
      },
    });
  };

  const initialValues: AssessmentsSectionFormValues = useMemo(
    () => ({
      hideReports: company.hideReports
        ? EnableDisableValue.Disabled
        : EnableDisableValue.Enabled,
      managerReport: {
        label: company.managerReport?.name,
        value: company.managerReport?.id,
        labelDescription:
          company.managerReport?.id === defaultManagerReportId
            ? 'Default'
            : undefined,
      },
      candidateReport: {
        label: company.candidateReport?.name,
        value: company.candidateReport?.id,
        labelDescription:
          company.candidateReport?.id === defaultCandidateReportId
            ? 'Default'
            : undefined,
      },
      reasonableAdjustments: company.reasonableAdjustmentsOption.kind,
      assessmentExpiry: company.assessmentExpiryOption
        ? EnableDisableValue.Enabled
        : EnableDisableValue.Disabled,
      assessmentExpiryDuration: company.assessmentExpiryOption
        ? {
            value: company.assessmentExpiryOption.duration,
            label: getAssessmentExpiryUnitString(
              company.assessmentExpiryOption.duration,
              company.assessmentExpiryOption.unit
            ),
          }
        : undefined,
      contactCompanyWebsiteUrl:
        company.reasonableAdjustmentsOption.kind ===
        ReasonableAdjustmentsOptionKind.ContactCompanyUrl
          ? company.reasonableAdjustmentsOption.value || ''
          : undefined,
      contactCompanyEmail:
        company.reasonableAdjustmentsOption.kind ===
        ReasonableAdjustmentsOptionKind.ContactCompanyEmail
          ? company.reasonableAdjustmentsOption.value || ''
          : undefined,
      generateManagerReport: company.generateManagerReport
        ? EnableDisableValue.Enabled
        : EnableDisableValue.Disabled,
      generateCandidateReport: company.generateCandidateReport
        ? EnableDisableValue.Enabled
        : EnableDisableValue.Disabled,
      emailCandidateReport: company.emailCandidateReport ?? false,
      projectTeamEmailSettingsMode:
        projectTeamEmailToOptionsMap[company.projectTeamEmailSettings.mode] ??
        projectTeamEmailToOptionsMap[ProjectTeamEmailMode.Disabled],
      projectTeamEmailRoles:
        company.projectTeamEmailSettings.actorRoles.map(actorRole => ({
          label: actorRoleLabels[actorRole],
          value: actorRole,
        })) ?? [],
      cognitiveTests: company.areCognitiveTestsBlocked
        ? EnableDisableValue.Disabled
        : EnableDisableValue.Enabled,
      multiAssessmentStages: company.areMultiAssessmentStagesEnabled
        ? EnableDisableValue.Enabled
        : EnableDisableValue.Disabled,
      mandatoryPersonalityTest: company.isPersonalityTestMandatory
        ? EnableDisableValue.Enabled
        : EnableDisableValue.Disabled,
      aiInterview: company.isAiInterviewEnabled
        ? EnableDisableValue.Enabled
        : EnableDisableValue.Disabled,
    }),
    [company, defaultManagerReportId, defaultCandidateReportId]
  );

  return (
    <Formik<AssessmentsSectionFormValues>
      initialValues={initialValues}
      onSubmit={handleSave}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      validateOnMount={false}
    >
      <FormFields
        company={company}
        onCancelClick={onCancelClick}
        defaultManagerReportId={defaultManagerReportId}
        defaultCandidateReportId={defaultCandidateReportId}
      />
    </Formik>
  );
}

function FormFields({
  company,
  onCancelClick,
  defaultManagerReportId,
  defaultCandidateReportId,
}: {
  company: CompanyPageCompanyFragment;
  onCancelClick: () => void;
  defaultManagerReportId: number | undefined;
  defaultCandidateReportId: number | undefined;
}) {
  const {
    submitForm,
    values,
    errors,
    setFieldValue,
    setFieldError,
  } = useFormikContext<AssessmentsSectionFormValues>();
  const [notificationVisibility, setNotificationVisibility] =
    useState<NotificationVisibility>({});

  useEffect(() => {
    if (
      values.projectTeamEmailSettingsMode.value !==
        ProjectTeamEmailMode.ProjectRoleBased &&
      values.projectTeamEmailSettingsMode.value !==
        ProjectTeamEmailMode.ProjectRoleAndAssigneeBased
    ) {
      setFieldError('projectTeamEmailRoles', undefined);
    }
  }, [
    errors.projectTeamEmailRoles,
    values.projectTeamEmailSettingsMode.value,
    setFieldError,
  ]);

  const handleReasonableAdjustmentsChange = () => {
    setNotificationVisibility({
      reasonableAdjustments: true,
    });
  };

  const handleCognitiveTestsChange = () => {
    setNotificationVisibility(prev => ({
      ...prev,
      cognitiveTests: true,
    }));
  };

  const handleMultiAssessmentStagesChange = () => {
    setNotificationVisibility((prev) => ({
      ...prev,
      multiAssessmentStages: true,
    }));
  };

  const handleMandatoryPersonalityTestChange = () => {
    setNotificationVisibility((prev) => ({
      ...prev,
      mandatoryPersonalityTest: true,
    }));
  };

  const handleAiInterviewChange = () => {
    setNotificationVisibility((prev) => ({
      ...prev,
      aiInterview: true,
    }));
  };

  const actorRolesOptions = parseActorRolesForSelect();

  return (
    <EditFormWrapper
      onCancelClick={onCancelClick}
      onSaveClick={submitForm}
      title="Edit Assessments"
      formId="assessments-section-form"
      hasResetAllButton={false}
      data-testid={dataTestId}
    >
      <form
        onSubmit={e => e.preventDefault()}
        id="assessments-section-form-element"
        className={styles.form}
      >
        <FormikRadioButtonGroup
          id="hideReports"
          name="hideReports"
          options={yesNoRadioButtonOptions}
          layout="horizontal"
          label="Allow company users to access report enablement settings and view reports via the company app"
          useFormikField={useField}
        />
        <div className={styles.reportSelectContainer}>
          <FormikRadioButtonGroup
            id="generateManagerReport"
            name="generateManagerReport"
            options={generateRadioButtonOptions}
            layout="horizontal"
            label="Hiring team report generation"
            useFormikField={useField}
            className={styles.radioGroup}
          />
          <ReportSelect
            companyId={company.id}
            subType={ReportSubType.StageResultsManager}
            name="managerReport"
            id="managerReport"
            label="Hiring team report"
            dataTestId={dataTestId}
            companyDefaultReportId={defaultManagerReportId}
          />
          <FormikRadioButtonGroup
            id="generateCandidateReport"
            name="generateCandidateReport"
            options={generateRadioButtonOptions}
            layout="horizontal"
            label="Candidate report generation"
            useFormikField={useField}
            onChange={() => {
              setFieldValue('emailCandidateReport', false);
            }}
            className={styles.radioGroup}
          />
          {values.generateCandidateReport === EnableDisableValue.Enabled && (
            <FormikCheckboxFormField
              id="emailCandidateReport"
              label="Email candidate the report"
              useFormikField={useField}
              className={styles.emailCandidateReportCheckbox}
            />
          )}
          <ReportSelect
            companyId={company.id}
            subType={ReportSubType.StageResultsCandidate}
            name="candidateReport"
            id="candidateReport"
            label="Candidate report"
            dataTestId={dataTestId}
            companyDefaultReportId={defaultCandidateReportId}
          />
          <ReasonableAdjustments
            onChange={handleReasonableAdjustmentsChange}
            companyName={company.name}
            notificationVisibility={
              notificationVisibility.reasonableAdjustments ?? false
            }
          />
        </div>
        <div className={styles.assessmentExpiryContainer}>
          <FormikRadioButtonGroup
            id="assessmentExpiry"
            name="assessmentExpiry"
            options={enableDisableRadioButtonOptions}
            layout="horizontal"
            label="Project-level assessment expiry notification"
            data-testid={`${dataTestId}__assessment-expiry-radio-group`}
            useFormikField={useField}
            className={styles.radioGroup}
            aria-label="Assessment expiry"
            onChange={value => {
              setNotificationVisibility({
                assessmentExpiry: true,
              });
              if (value === EnableDisableValue.Enabled) {
                setFieldValue(
                  'assessmentExpiryDuration',
                  assessmentExpiryDurationOptions[0]
                );
              }
            }}
          />
          {values.assessmentExpiry === EnableDisableValue.Enabled && (
            <FormikSelectFormField<number>
              id="assessmentExpiryDuration"
              name="assessmentExpiryDuration"
              label="Project-level assessment expiry duration"
              placeholder="Project-level assessment expiry duration"
              useFormikField={useField}
              options={assessmentExpiryDurationOptions}
              data-testid={`${dataTestId}__assessment-expiry-duration-select`}
              aria-label="Project-level assessment expiry duration"
              hasClearIndicator={false}
              bottomText={errors.assessmentExpiryDuration}
            />
          )}
          {notificationVisibility.assessmentExpiry && (
            <InlineNotification
              notificationType={NotificationType.NEUTRAL}
              className={styles.inlineNotification}
            >
              {
                assessmentExpiryEnablingNotificationMessages[
                  values.assessmentExpiry
                ]
              }
            </InlineNotification>
          )}
        </div>
        <div className={styles.projectTeamEmailSettingsSection}>
          <h3 className={styles.heading}>Assessment completion emails</h3>
          <div>
            <FormikSelectFormField
              id="projectTeamEmailSettingsMode"
              label="Mode"
              options={projectTeamEmailModeOptions}
              useFormikField={useField}
              hasClearIndicator={false}
              onChange={() => {
                setFieldValue('projectTeamEmailRoles', []);
              }}
            />
          </div>
          <div>
            <FormikMultiselectFormField
              id="projectTeamEmailRoles"
              label="Hiring team email roles"
              placeholder="Hiring team email roles"
              isDisabled={
                values.projectTeamEmailSettingsMode.value ===
                  ProjectTeamEmailMode.ProjectRoleBased ||
                values.projectTeamEmailSettingsMode.value ===
                  ProjectTeamEmailMode.ProjectRoleAndAssigneeBased
                  ? false
                  : true
              }
              options={actorRolesOptions}
              useFormikField={useField}
            />
          </div>
        </div>
        <div>
          <FormikRadioButtonGroup
            id="cognitiveTests"
            name="cognitiveTests"
            options={enableDisableRadioButtonOptions}
            layout="horizontal"
            label="Cognitive tests"
            data-testid={`${dataTestId}__cognitive-tests-radio-group`}
            useFormikField={useField}
            className={styles.radioGroup}
            onChange={handleCognitiveTestsChange}
          />
          {notificationVisibility.cognitiveTests && (
            <InlineNotification
              notificationType={NotificationType.NEUTRAL}
              className={styles.inlineNotification}
            >
              The change will{' '}
              {values.cognitiveTests === EnableDisableValue.Disabled
                ? 'disable'
                : 'enable'}{' '}
              the creation of cognitive tests for new projects created for this
              company, through the RSP builder.
            </InlineNotification>
          )}
        </div>
        <div>
          <FormikRadioButtonGroup
            id="multiAssessmentStages"
            name="multiAssessmentStages"
            options={enableDisableRadioButtonOptions}
            layout="horizontal"
            label="Multiple assessment stages per role"
            data-testid={`${dataTestId}__multi-assessment-stages-radio-group`}
            useFormikField={useField}
            className={styles.radioGroup}
            onChange={handleMultiAssessmentStagesChange}
          />
          {notificationVisibility.multiAssessmentStages && (
            <InlineNotification
              notificationType={NotificationType.NEUTRAL}
              className={styles.inlineNotification}
            >
              The change will{' '}
              {values.multiAssessmentStages === EnableDisableValue.Disabled
                ? 'disable'
                : 'enable'}{' '}
              adding more than one assessment stage to a role for this company.
              Roles that already have several keep working either way.
            </InlineNotification>
          )}
        </div>
        <div>
          <FormikRadioButtonGroup
            id="mandatoryPersonalityTest"
            name="mandatoryPersonalityTest"
            options={enableDisableRadioButtonOptions}
            layout="horizontal"
            label="Personality assessment mandatory"
            data-testid={`${dataTestId}__mandatory-personality-test-radio-group`}
            useFormikField={useField}
            className={styles.radioGroup}
            onChange={handleMandatoryPersonalityTestChange}
          />
          {notificationVisibility.mandatoryPersonalityTest && (
            <InlineNotification
              notificationType={NotificationType.NEUTRAL}
              className={styles.inlineNotification}
            >
              The change will{' '}
              {values.mandatoryPersonalityTest === EnableDisableValue.Disabled
                ? 'stop pre-selecting'
                : 'pre-select and lock'}{' '}
              the personality assessment on the first assessment stage of a role
              for this company. Existing roles are not changed.
            </InlineNotification>
          )}
        </div>
        <div>
          <FormikRadioButtonGroup
            id="aiInterview"
            name="aiInterview"
            options={enableDisableRadioButtonOptions}
            layout="horizontal"
            label="AI interview stages"
            data-testid={`${dataTestId}__ai-interview-radio-group`}
            useFormikField={useField}
            className={styles.radioGroup}
            onChange={handleAiInterviewChange}
          />
          {notificationVisibility.aiInterview && (
            <InlineNotification
              notificationType={NotificationType.NEUTRAL}
              className={styles.inlineNotification}
            >
              The change will{' '}
              {values.aiInterview === EnableDisableValue.Disabled
                ? 'hide'
                : 'show'}{' '}
              AI interview stages in the role wizard and hiring process
              templates for this company. SZ admins and internal users always
              see them. Existing roles are not changed.
            </InlineNotification>
          )}
        </div>
      </form>
    </EditFormWrapper>
  );
}
